import React from "react";

export default () => (
  <div className="privacy-policy">
    <p>Effective Date: December 6, 2023</p>

    <p>
      Chabad on Campus International, Inc. (“<b>Chabad</b>,” “<b>we</b>,” “
      <b>our</b>,” or “<b>us</b>”) recognizes the importance of protecting the
      privacy of our online visitors. It is our intent to balance our legitimate
      business interests in collecting and using information received from and
      about you with your reasonable expectations of privacy. The following
      privacy policy (“<b>Privacy Policy</b>”) is the way we handle information
      learned about you. This Privacy Policy applies to any online location that
      links to this Privacy Policy, including the following (collectively the “
      <b>Sites</b>”):
    </p>

    <p>
      <ul>
        <li>Chabadoncampus.org</li>
        <li>Granddraw.org</li>
        <li>Jewishu.org</li>
      </ul>
    </p>

    <p>
      <strong>PLEASE REVIEW THIS PRIVACY POLICY CAREFULLY</strong>. When you
      submit information to or through the Site, you consent to the collection
      and processing of your information as described in this Privacy Policy. By
      using the Site, you accept the terms of this Privacy Policy and our Terms
      of Use. CHABAD IS LOCATED IN THE UNITED STATES AND INFORMATION COLLECTED
      THROUGH THE SITES WILL BE STORED IN THE UNITED STATES.
    </p>
    <p>
      If you are a data subject in the European Economic Area, please see the
      “Additional Disclosures for People in the European Economic Area” sections
      respectively. If you have any questions or wish to exercise your rights
      and choices, please contact us as set out in the “Contact Us” section.
    </p>

    <h1>1. Information Collection.</h1>

    <h2>A. Information You Provide.</h2>

    <p>
      We collect information about you when you interact with the Sites and
      voluntarily provide information to us, including:
    </p>

    <p>
      <ul>
        <li>
          <b>Contact Data</b>, including your first and last name, email
          address, postal address, and phone number.
        </li>
        <li>
          <b>Demographic Data</b>, including your age, gender, and country.
        </li>
        <li>
          <b>Donation Data</b>, including your payment instrument number (such
          as a credit or debit card number), expiration date, security code as
          necessary to process your payments, and donation amounts.
        </li>
        <li>
          <b>Medical Information</b>, including next of Kin, prescriptions,
          doctor’s notes for prescriptions, and medical conditions.
        </li>
      </ul>
    </p>

    <p>
      You may choose to voluntarily provide other information to us that we do
      not request, and, in such instances, you are solely responsible for such
      information.
    </p>

    <h2>B. Information Collected Automatically.</h2>

    <p>
      In addition, we automatically collect information when you use the Sites.
    </p>

    <p>
      <ul>
        <li>
          <b>Service Use Data</b>, including data about pages you visit, emails
          and advertisements you view, products and services you view and
          purchase, the time of day you browse, and your referring and exiting
          pages.
        </li>
        <li>
          <b>Device Data</b>, including data about the type of device or browser
          you use, your device’s operating software, your internet service
          provider, your device’s regional and language settings, and device
          identifiers such as IP address and Ad Id.
        </li>
        <li>
          <b>Location Data</b>, including imprecise location data (such as
          location derived from an IP address or data that indicates a city or
          postal code level) and, with your consent, precise location data (such
          as latitude/longitude data).
        </li>
      </ul>
    </p>

    <p>
      We use various tracking technologies to automatically collect information
      when you use the Sites, including the following:
    </p>

    <p>
      <ul>
        <li>
          <b>Log Files</b>, which are files that record events that occur in
          connection with your use of the Sites.
        </li>
        <li>
          <b>Cookies</b>, which are small data files stored on your device that
          act as a unique tag to identify your browser. We use two types of
          cookies: session cookies and persistent cookies. Session cookies make
          it easier for you to navigate our website and expire when you close
          your browser. Persistent cookies help with personalizing your
          experience, remembering your preferences, and supporting security
          features. Additionally, persistent cookies allow us to bring you
          advertising both on and off the Sites. Persistent cookies may remain
          on your device for extended periods of time, and generally may be
          controlled through your browser settings. Web browsers allow some
          control of most cookies through the browser settings. To find out more
          about cookies, including how to manage and delete cookies, visit
          &nbsp;
          <a
            href="http://www.allaboutcookies.org"
            target="_blank"
            rel="noopener noreferrer"
            className="pointer underline-text"
          >
            www.allaboutcookies.org
          </a>
          . Some web browsers provide settings that allow a user to reject
          cookies or to alert a user when a cookie is placed on the user’s
          computer, tablet or mobile device. Most mobile devices also offer
          settings to reject mobile device identifiers. Although users are not
          required to accept cookies or mobile device identifiers, blocking or
          rejecting them may prevent access to some features available through
          the Site.
        </li>
        <li>
          <b>Pixels</b> (also known as web beacons), which is code embedded in a
          website, video, email, or advertisement that sends information about
          your use to a server. There are various types of pixels, including
          image pixels (which are small graphic images) and JavaScript pixels
          (which contains JavaScript code). When you access a website, video,
          email, or advertisement that contains a pixel, the pixel may permit us
          or a separate entity to drop or read cookies on your browser. Pixels
          are used in combination with cookies to track activity by a particular
          browser on a particular device. We may incorporate pixels from
          separate entities that allow us track our conversions, bring you
          advertising both on and off the Sites, and provide you with additional
          functionality, such as the ability to connect our Sites with your
          social media account.
        </li>
      </ul>
    </p>

    <h2>C. Information from Other Sources.</h2>

    <p>We also collect information from other sources, including: </p>

    <p>
      <ul>
        <li>
          <b>Social networks</b>&nbsp; when you engage with our content,
          reference our Sites, or grant us permission to access information from
          the social networks.
        </li>
        <li>
          <b>Partners</b>&nbsp; that offer co-branded services or engage in
          joint marketing activities, including local Chabad houses throughout
          the world.
        </li>
        <li>
          <b>Publicly-available sources</b>, including data in the public
          domain.
        </li>
      </ul>
    </p>

    <h1>2. Use of Information.</h1>

    <p>
      We may use the information we collect for any of the following purposes:
    </p>

    <p>
      <ul>
        <li>Operate and manage our Sites.</li>
        <li>
          Perform services requested by you, such as respond to your comments,
          questions, and requests, and provide customer service.
        </li>
        <li>
          Sending you technical notices, updates, security alerts, information
          regarding changes to our policies, and support and administrative
          messages.
        </li>
        <li>
          Preventing and addressing fraud, breach of policies or terms, and
          threats or harm.
        </li>
        <li>Monitoring and analyzing trends, usage, and activities.</li>
        <li>
          Conducting raffles that you have voluntarily entered through donating
          or otherwise.
        </li>
        <li>Conducting research, including focus groups and surveys.</li>
        <li>
          Improving the Service or other Chabad websites, apps, marketing
          efforts, products and services.
        </li>
        <li>
          Developing and sending you direct marketing, including advertisements
          and communications about our and other entities’ products, offers,
          promotions, rewards, events, and services.
        </li>
        <li>
          Conducting promotions, including verifying your eligibility and
          delivering prizes in connection with your entries.
        </li>
        <li>
          Fulfilling any other business or commercial purposes at your direction
          or with your notice and/or consent.
        </li>
      </ul>
    </p>

    <p>
      Notwithstanding the above, we may use information that does not identify
      you (including information that has been aggregated or de-identified) for
      any purpose except as prohibited by applicable law. For information on
      your rights and choices regarding how we use information about you, please
      see the “Your Rights and Choices” section below.
    </p>

    <h1>3. Sharing of Information.</h1>

    <p>
      We share information we collect in accordance with the practices described
      in this Privacy Policy, and may share such information with the following
      third parties:
    </p>

    <p>
      <ul>
        <li>
          <b>Service Providers</b>. We share information with entities that
          process information on our behalf for our business purposes. Service
          providers assist us with services such as payment processing, data
          analytics, marketing and advertising, website hosting, and technical
          support.
        </li>
        <li>
          <b>Grant-Making Organizations</b>. We are a non-profit organization
          that relies on funding from organizations that provide grant funding
          and we may need to share certain information collected in the process
          of applying for a respective grant.
        </li>
        <li>
          <b>Partners</b>. We share information with our partners in connection
          with offering our services including raffles and the ability to sign
          up for Chabad arranged trips.
        </li>
        <li>
          <b>Merger or Acquisition</b>. We share information in connection with,
          or during negotiations of, any proposed or actual merger, purchase,
          sale or any other type of acquisition or business combination of all
          or any portion of our assets, or transfer of all or a portion of our
          business to another business.
        </li>
        <li>
          <b>Security and Compelled Disclosure</b>. We share information to
          comply with the law or other legal process, and where required, in
          response to lawful requests by public authorities, including to meet
          national security or law enforcement requirements. We also share
          information to protect the rights, property, life, health, security
          and safety of us, the Sites, or anyone else.
        </li>
        <li>
          <b>Consent</b>. We share information with notice to you and your
          consent.
        </li>
        <li>
          <b>Mobile Information</b>. No mobile information will be shared 
          with third parties/affiliates for marketing/promotional purposes. Information sharing 
          to subcontractors in support services, such as customer service is permitted. 
          All other use case categories exclude text messaging originator opt-in data and consent;
           this information will not be shared with any third parties.
        </li>
      </ul>
    </p>

    <p>
      Notwithstanding the above, we may share information that does not identify
      you (including information that has been aggregated or de-identified)
      except as prohibited by applicable law. For information on your rights and
      choices regarding how we share information about you, please see the “Your
      Rights and Choices” section below.
    </p>

    <h1>4. Social Media and Technology Integrations.</h1>

    <p>
      We offer parts of our Sites through websites, platforms, and services
      operated or controlled by separate entities. In addition, we integrate
      technologies operated or controlled by separate entities into parts of our
      Sites. Some examples include:
    </p>

    <p>
      <ul>
        <li>
          <b>Links</b>. Our Sites includes links that hyperlink to websites,
          platforms, and other services not operated or controlled by us.
        </li>
        <li>
          <b>Liking, Sharing, and Logging-In</b>. We may embed a pixel or SDK on
          our Sites that allows you to “like” or “share” content on, or log-in
          to your account through social media. If you choose to engage with
          such integration, we may receive information from the social network
          that you have authorized to share with us. Please note that the social
          network may independently collect information about you through the
          integration.
        </li>
      </ul>
    </p>

    <p>
      Please note that when you interact with other entities, including when you
      leave our Sites, those entities may independently collect information
      about you and solicit information from you. The information collected and
      stored by those entities remains subject to their own policies and
      practices, including what information they share with us, your rights and
      choices on their services and devices, and whether they store information
      in the U.S. or elsewhere. We encourage you to familiarize yourself with
      and consult their privacy policies and terms of use.
    </p>

    <h1>5. Your Rights and Choices.</h1>

    <h2>A. Tracking Technology Choices.</h2>

    <p>
      <ul>
        <li>
          <b>Cookies and Pixels</b>. Most browsers accept cookies by default.
          You can instruct your browser, by changing its settings, to decline or
          delete cookies. If you use multiple browsers on your device, you will
          need to instruct each browser separately. Your ability to limit
          cookies is subject to your browser settings and limitations.
        </li>
        <li>
          <b>Do Not Track</b>. Your browser settings may allow you to
          automatically transmit a “Do Not Track” signal to online services you
          visit. Our Sites do not currently take any actions when it receives a
          “Do Not Track” request. For more information on “Do Not Track,”
          including how to turn on “Do Not Track,” please visit &nbsp;
          <a
            href="http://www.allaboutdnt.com"
            target="_blank"
            rel="noopener noreferrer"
            className="pointer underline-text"
          >
            http://www.allaboutdnt.com
          </a>
          .
        </li>
        <li>
          <b>App and Location Technologies</b>. You can stop all collection of
          information via an app by uninstalling the app. You can also reset
          your device Ad Id at any time through your device settings, which is
          designed to allow you to limit the use of information collected about
          you. You can stop all collection of precise location data through an
          app by uninstalling the app or withdrawing your consent through your
          device settings.
        </li>
        <li>
          <b>Texting and SMS Messaging</b>. We may use your mobile phone number to send you messages about our services.  Messages will be sent on average 10 times per month. Message and data rates may apply. We will only send you marketing SMS or text messages if you agree to receive such messages.  If you receive an unwanted text message from us, you may opt out of receiving future text messages from us by following the instructions in the text message you have received from us or by otherwise contacting us as set forth in “Contact Us” below.
        </li>
      </ul>
      <p></p>
      Please be aware that if you disable or remove tracking technologies some
      parts of the Sites may not function correctly.
    </p>

    <h2>B. Analytics and Interest-Based Advertising.</h2>

    <p>
      We use analytics services, such as Google Analytics, to help us understand
      how users access and use the Sites. In addition, we work with agencies,
      advertisers, ad networks, and other technology services to place ads about
      our products and services on other websites and services. For example, we
      place ads through Google and Facebook that you may view on their platforms
      as well as on other websites and services.
    </p>

    <p>
      As part of this process, we may incorporate tracking technologies into our
      own Sites (including our website and emails) as well as into our ads
      displayed on other websites and services. Some of these tracking
      technologies may track your activities across time and services for
      purposes of associating the different devices you use, and delivering
      relevant ads and/or other content to you (“
      <b>Interest-based Advertising</b>”).
    </p>

    <p>
      Google provides tools to allow you to opt out of the use of certain
      information collected by Google Analytics at &nbsp;
      <a
        href="https://tools.google.com/dlpage/gaoptout"
        target="_blank"
        rel="noopener noreferrer"
        className="pointer underline-text"
      >
        https://tools.google.com/dlpage/gaoptout
      </a>
      .
    </p>

    <p>
      Please note that if you opt out using any of these methods, the opt out
      will only apply to the specific browser or device from which you opt out.
      We are not responsible for the effectiveness of, or compliance with, any
      opt out options or programs, or the accuracy of any other entities’
      statements regarding their opt out options or programs.
    </p>

    <h2>C. Email Communications.</h2>

    <p>
      You can opt-out of receiving promotional emails from us at any time by
      following the instructions as provided in emails to click on the
      unsubscribe link, or emailing us at the email address set out in the
      “Contact Us” section below with the word UNSUBSCRIBE in the subject field
      of the email. Please note that you cannot opt-out of non-promotional
      emails, such as those about your transactions, servicing, or Chabad's
      ongoing business relations. Please note that your opt out is limited to
      the email address, device, and phone number used and will not affect
      subsequent subscriptions.
    </p>

    <h1>6. Children.</h1>

    <p>
      The Sites are intended for a general audience, and is not directed at
      children under (13) years of age.
    </p>

    <h1>7. Data Security.</h1>

    <p>
      We implement and maintain reasonable security measures designed to prevent
      unauthorized intrusion to the Sites and the alteration, acquisition or
      misuse of personal information, however, we will not be responsible for
      loss, corruption or unauthorized acquisition or misuse of personal
      information that you provide through the Sites that is stored by us, or
      for any damages resulting from such loss, corruption or unauthorized
      acquisition or misuse. We will retain your personal information for as
      long as necessary to fulfill the purpose for which it was collected, or as
      required by applicable laws or regulation.
    </p>

    <h1>8. International Transfer.</h1>

    <p>
      We are based in the U.S. and the information we collect is governed by
      U.S. law. If you are accessing the Sites from outside of the U.S., please
      be aware that information collected through the Sites may be transferred
      to, processed, stored, and used in the U.S. and other jurisdictions. Data
      protection laws in the U.S. and other jurisdictions may be different from
      those of your country of residence. Your use of the Sites or provision of
      any information therefore constitutes your consent to the transfer to and
      from, processing, usage, sharing, and storage of information about you in
      the U.S. and other jurisdictions as set out in this Privacy Policy.
    </p>

    <h1>9. Changes to this Privacy Policy.</h1>

    <p>
      We reserve the right to revise and reissue this Privacy Policy at any
      time. Any changes will be effective immediately upon posting of the
      revised Privacy Policy. Your continued use of our Sites indicate your
      consent to the Privacy Policy then posted. If the changes are material, we
      may provide you additional notice to your email address.
    </p>

    <h1>10. Contact Us.</h1>

    <p>
      If you have any questions or comments about this Privacy Policy, our data
      practices, or our compliance with applicable law, please contact us:
    </p>

    <p>
      <b>By email:</b>
    </p>

    <p>help@chabadoncampus.org</p>

    <p>
      <b>By mail:</b>
    </p>

    <p>
      <p style={{ marginBottom: "0px" }}>ATTN: Privacy</p>
      <p style={{ marginBottom: "0px" }}>719 Eastern Parkway, Suite 1</p>
      <p>Brooklyn, NY 11213</p>
    </p>

    <h1>
      11. Additional Disclosures for People in the European Economic Area.
    </h1>

    <h2>A. Roles.</h2>

    <p>
      Data protection laws in the European Economic Area (“EEA”) distinguish
      between organizations that process personal data for their own purposes
      (known as “controllers”) and organizations that process personal data on
      behalf of other organizations (known as “processors”). Chabad acts as a
      controller with respect to personal data collected as you interact with
      our websites, emails, and advertisements.
    </p>

    <h2>B. Lawful Basis for Processing.</h2>

    <p>
      Our lawful bases for processing personal data include where: (a) you have
      given consent to the processing for one or more specific purposes, either
      to us or to our partners; (b) processing is necessary for the performance
      of a contract with you; (c) processing is necessary for compliance with a
      legal obligation; or (d) processing is necessary for the purposes of the
      legitimate interests pursued by us or a third party, and your interests
      and fundamental rights and freedoms do not override those interests.
    </p>

    <h2>C. Your Data Subject Rights.</h2>

    <p>
      If you are a data subject in the EEA, you have the right to access,
      rectify, or erase any personal data we have collected about you. You also
      have the right to data portability and the right to restrict or object to
      our processing of personal data we have collected about you. In addition,
      you have the right to ask us not to process your personal data (or provide
      it to third parties to process) for marketing purposes or purposes
      materially different than for which it was originally collected or
      subsequently authorized by you. You may withdraw your consent at any time
      for any data processing we do based on consent you have provided to us.
    </p>

    <p>
      You may exercise your rights by submitting a written request to us at the
      address set out in the “Contact Us” section above. We will respond to your
      request within 30 days. We may request specific information from you to
      help us confirm your identity and process your request. Applicable law may
      require or permit us to decline your request. If we decline your request,
      we will tell you why, subject to legal restrictions.
    </p>

    <p>
      Please note that we retain information as necessary to fulfill the
      purposes for which it was collected, and may continue to retain and use
      information even after a data subject request for purposes of our
      legitimate interests, including as necessary to comply with our legal
      obligations, resolve disputes, prevent fraud, and enforce our agreements.
    </p>

    <h2>D. Complaints.</h2>

    <p>
      If you have a complaint about our use of your personal data or response to
      your requests regarding your personal data, you may submit a complaint to
      the data protection regulator in your jurisdiction. We would, however,
      appreciate the opportunity to address your concerns before you approach a
      data protection regulator, and would welcome you directing an inquiry
      first to us. Please refer to the section entitled “Contact Us” for contact
      information to send us any questions.
    </p>
  </div>
);
